import React, { useState } from 'react';
import { Mail, Download, AlertCircle, Copy, Check } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../utils/cn';

interface ExportPanelProps {
  results: any[];
  onExportCSV: () => void;
}

const ExportPanel: React.FC<ExportPanelProps> = ({ results, onExportCSV }) => {
  const [emailAddress, setEmailAddress] = useState('');
  const [showError, setShowError] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const formatNumberOrDefault = (value: any, decimals: number = 2): string => {
    if (value == null) return '-';
    const num = Number(value);
    return isNaN(num) ? '-' : num.toFixed(decimals);
  };

  const generateEmailContent = () => {
    const subject = 'スプリント分析結果';
    const body = results.map(result => 
      `日付: ${format(parseISO(result.measurement_date), 'yyyy/MM/dd')}\n` +
      `名前: ${result.name}\n` +
      `スピード: ${formatNumberOrDefault(result.data.speed)} m/s\n` +
      `ピッチ: ${formatNumberOrDefault(result.data.pitch)} 歩/秒\n` +
      `ストライド: ${formatNumberOrDefault(result.data.stride)} m\n` +
      `接地時間: ${formatNumberOrDefault(result.data.contactTime, 3)} 秒\n` +
      `滞空時間: ${formatNumberOrDefault(result.data.flightTime, 3)} 秒\n` +
      `100m予測: ${formatNumberOrDefault(result.data.predicted100m)} 秒\n` +
      (result.comment ? `コメント: ${result.comment}\n` : '') +
      `\n`
    ).join('\n');

    return { subject, body };
  };

  const handleSendEmail = async () => {
    if (!emailAddress || !validateEmail(emailAddress)) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setIsSending(true);

    try {
      const { subject, body } = generateEmailContent();
      
      // First, copy the content to clipboard
      await handleCopyToClipboard();
      
      // Then try to open mail client
      const mailtoLink = `mailto:${encodeURIComponent(emailAddress)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;

      // Show success message
      alert('メールの内容をクリップボードにコピーしました。\nメールアプリが開かない場合は、手動でメールを作成し、内容を貼り付けてください。');

    } catch (err) {
      console.error('Email failed:', err);
      alert('メール送信の準備に失敗しました。「内容をコピー」を使用してください。');
    } finally {
      setIsSending(false);
      setEmailAddress('');
    }
  };

  const handleCopyToClipboard = async () => {
    const { subject, body } = generateEmailContent();
    const fullContent = `件名: ${subject}\n\n${body}`;
    
    try {
      await navigator.clipboard.writeText(fullContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch (err) {
      console.error('Clipboard API failed:', err);
      
      // Fallback method
      try {
        const textarea = document.createElement('textarea');
        textarea.value = fullContent;
        textarea.style.position = 'fixed';
        textarea.style.left = '0';
        textarea.style.top = '0';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        
        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          return true;
        }
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
    }
    
    alert('テキストのコピーに失敗しました。手動でコピーしてください。');
    return false;
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg space-y-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">データのエクスポート</h3>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            メールアドレス
          </label>
          <div className="mt-1">
            <input
              type="email"
              id="email"
              name="email"
              placeholder="example@domain.com"
              value={emailAddress}
              onChange={(e) => {
                setEmailAddress(e.target.value);
                setShowError(false);
              }}
              className={cn(
                "block w-full rounded-md shadow-sm sm:text-sm",
                showError
                  ? "border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              )}
            />
          </div>
          {showError && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              有効なメールアドレスを入力してください
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleSendEmail}
            disabled={isSending}
            className={cn(
              "btn flex-1 flex items-center justify-center space-x-2",
              isSending ? "btn-secondary" : "btn-primary"
            )}
          >
            <Mail className="w-4 h-4" />
            <span>{isSending ? '送信中...' : 'メールで送信'}</span>
          </button>

          <button
            onClick={() => handleCopyToClipboard()}
            className={cn(
              "btn btn-secondary flex-1 flex items-center justify-center space-x-2",
              copied && "bg-green-50 text-green-600"
            )}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                <span>コピーしました</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>内容をコピー</span>
              </>
            )}
          </button>

          <button
            onClick={onExportCSV}
            className="btn btn-secondary flex-1 flex items-center justify-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>CSVでダウンロード</span>
          </button>
        </div>

        <div className="text-sm text-gray-500">
          ※ メール送信時に自動的にデータがクリップボードにコピーされます。<br />
          ※ メールアプリが開かない場合は、手動でメールを作成し、コピーした内容を貼り付けてください。
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;